import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Bot, Plus, Loader2 } from 'lucide-react';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import { useAgents, useFeedaAgents } from '../../hooks';
import sessionsService from '../../services/sessionsServices';
import Toast from '../Toast';
import AccentButton from '../ui/AccentButton';
import {
  DEFAULT_COPILOT,
  DEFAULT_COPILOT_ID,
  DEFAULT_COPILOT_NAME,
  findDefaultCopilot,
  isDefaultCopilot,
  toDefaultAwareAgentOption,
} from '../../constants/defaultCopilot';
import { PLATFORM_ID } from '../../constants/platform';

function toAgentOption(agent) {
  return toDefaultAwareAgentOption(agent);
}

function findAgentById(agents, agentId) {
  if (!agents?.length || agentId == null || agentId === '') return null;
  return agents.find(a => String(a.id ?? a.agent_id) === String(agentId)) || null;
}

const agentSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    minWidth: 160,
    backgroundColor: '#19191b',
    borderColor: state.isFocused ? '#00dc49' : 'rgba(255, 255, 255, 0.12)',
    borderRadius: 9999,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(163, 67, 255, 0.06)' : '0 4px 24px rgba(0, 0, 0, 0.06)',
    fontSize: 14,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.6 : 1,
    '&:hover': { borderColor: state.isFocused ? '#00dc49' : 'rgba(255, 255, 255, 0.2)' },
  }),
  valueContainer: base => ({ ...base, padding: '0 8px' }),
  input: base => ({ ...base, color: '#ffffff', margin: 0, padding: 0 }),
  singleValue: base => ({ ...base, color: '#ffffff', margin: 0 }),
  placeholder: base => ({ ...base, color: '#8d8d8d', margin: 0 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, color: '#8d8d8d', padding: '0 8px', '&:hover': { color: '#ffffff' } }),
  menu: base => ({
    ...base,
    backgroundColor: '#19191b',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 12,
    overflow: 'hidden',
    zIndex: 50,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
  }),
  menuList: base => ({ ...base, padding: 4, maxHeight: 240 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#00dc49' : state.isFocused ? 'rgba(255, 255, 255, 0.06)' : 'transparent',
    color: '#ffffff',
    fontSize: 14,
    cursor: 'pointer',
    borderRadius: 8,
    padding: '8px 10px',
  }),
  noOptionsMessage: base => ({ ...base, color: '#8d8d8d', fontSize: 13 }),
  groupHeading: base => ({
    ...base,
    color: '#8d8d8d',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '8px 10px 4px',
  }),
};

function getAgentIconSrc(agent) {
  if (!agent?.icon) return null;
  if (typeof agent.icon !== 'string') return null;
  if (
    agent.icon.startsWith('/') ||
    agent.icon.startsWith('http://') ||
    agent.icon.startsWith('https://') ||
    agent.icon.startsWith('data:')
  ) {
    if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://')) {
      // Legacy SVG uploads are served by S3 as binary/octet-stream and won't render in <img>.
      return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
    }
    return agent.icon;
  }
  return `data:image/png;base64,${agent.icon}`;
}

export default function PolyChatHeader() {
  const navigate = useNavigate();
  const { id: routeSessionId } = useParams();
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const { sessionAgent, selectedAgent, updateAgent, setChatmessages, setSessionAgent, setSessionID } =
    useContext(TemporaryChatContext);

  const { data: agentsData, isLoading: agentsLoading, isFetched: agentsFetched } = useAgents();
  const {
    data: feedaData,
    isLoading: feedaLoading,
    isFetched: feedaFetched,
  } = useFeedaAgents();

  const userAgents = agentsData?.agents || [];
  const feedaAgents = feedaData?.agents || [];
  const allAgents = useMemo(() => [...feedaAgents, ...userAgents], [feedaAgents, userAgents]);
  const agentsReady = agentsFetched && feedaFetched;
  const agentsLoadingAny = agentsLoading || feedaLoading;

  const agentOptions = useMemo(() => {
    const seen = new Set();

    const floFromApi =
      findDefaultCopilot(allAgents) || findAgentById(allAgents, DEFAULT_COPILOT_ID);
    const floOption = floFromApi ? toAgentOption(floFromApi) : { ...DEFAULT_COPILOT };
    seen.add(String(floOption.value));

    const adminOptions = [floOption];
    for (const agent of feedaAgents) {
      const option = toAgentOption(agent);
      if (!option?.value) continue;
      const key = String(option.value);
      if (seen.has(key)) continue;
      seen.add(key);
      adminOptions.push(option);
    }

    const userOptions = [];
    for (const agent of userAgents) {
      const option = toAgentOption(agent);
      if (!option?.value) continue;
      const key = String(option.value);
      if (seen.has(key)) continue;
      seen.add(key);
      userOptions.push(option);
    }

    const groups = [];
    if (adminOptions.length) {
      groups.push({ label: 'Admin created', options: adminOptions });
    }
    if (userOptions.length) {
      groups.push({ label: 'User created', options: userOptions });
    }
    return groups;
  }, [feedaAgents, userAgents, allAgents]);

  const flatAgentOptions = useMemo(
    () => agentOptions.flatMap(group => group.options || []),
    [agentOptions],
  );

  // Wait for agent list APIs, then hydrate default/selected agent with exact API icon + metadata.
  useEffect(() => {
    if (!agentsReady) return;
    if (selectedAgent?.mode === 'team') return;

    const currentId = selectedAgent?.value || sessionAgent?.value || DEFAULT_COPILOT_ID;
    const lookingForDefaultFlo =
      !currentId ||
      isDefaultCopilot(selectedAgent) ||
      isDefaultCopilot(sessionAgent) ||
      String(currentId) === DEFAULT_COPILOT_ID;

    const fromApi =
      findAgentById(allAgents, currentId) ||
      (lookingForDefaultFlo ? findDefaultCopilot(allAgents) || findAgentById(allAgents, DEFAULT_COPILOT_ID) : null);

    const agentObj = fromApi ? toAgentOption(fromApi) : lookingForDefaultFlo ? { ...DEFAULT_COPILOT } : null;
    if (!agentObj) return;

    const selectedNeedsUpdate =
      String(selectedAgent?.value) !== String(agentObj.value) ||
      selectedAgent?.icon !== agentObj.icon ||
      selectedAgent?.label !== agentObj.label;

    if (selectedNeedsUpdate) {
      updateAgent(agentObj);
    }

    // On an active session with a different agent (from history), don't overwrite sessionAgent.
    const sessionMatchesCurrent =
      !routeSessionId ||
      String(sessionAgent?.value) === String(currentId) ||
      isDefaultCopilot(sessionAgent);

    if (
      sessionMatchesCurrent &&
      (String(sessionAgent?.value) !== String(agentObj.value) ||
        sessionAgent?.icon !== agentObj.icon ||
        sessionAgent?.label !== agentObj.label)
    ) {
      setSessionAgent(agentObj);
    }
  }, [
    allAgents,
    agentsReady,
    routeSessionId,
    selectedAgent,
    sessionAgent,
    updateAgent,
    setSessionAgent,
  ]);

  // Ensure Xavi stays selected by default when nothing valid is chosen yet.
  useEffect(() => {
    if (!agentsReady) return;
    if (selectedAgent?.mode === 'team') return;
    if (selectedAgent?.value) return;
    updateAgent({ ...DEFAULT_COPILOT });
    setSessionAgent({ ...DEFAULT_COPILOT });
  }, [agentsReady, selectedAgent, updateAgent, setSessionAgent]);

  const selectedOption = useMemo(() => {
    const currentValue = selectedAgent?.value || sessionAgent?.value || DEFAULT_COPILOT_ID;
    const found = flatAgentOptions.find(option => String(option.value) === String(currentValue));
    if (found) return found;
    const raw = selectedAgent?.value ? selectedAgent : sessionAgent;
    if (raw?.value) return toDefaultAwareAgentOption(raw) || raw;
    return flatAgentOptions[0] || { ...DEFAULT_COPILOT };
  }, [flatAgentOptions, selectedAgent, sessionAgent]);

  // Prefer branded Xavi option (or other selected option) over stale context.
  const displayAgent = useMemo(() => {
    if (selectedOption) {
      return {
        label: selectedOption.label,
        value: selectedOption.value,
        icon: selectedOption.icon,
        mode: selectedOption.mode,
      };
    }
    const raw = sessionAgent?.label ? sessionAgent : selectedAgent;
    return isDefaultCopilot(raw) ? toDefaultAwareAgentOption(raw) : raw;
  }, [selectedOption, sessionAgent, selectedAgent]);

  const iconSrc = useMemo(() => getAgentIconSrc(displayAgent), [displayAgent]);
  const waitingForAgentData = !agentsReady;

  const startSessionWith = async agentObj => {
    setSwitching(true);
    try {
      setChatmessages([]);
      updateAgent(agentObj);
      setSessionAgent(agentObj);
      const sessionRes = await sessionsService.createSession({
        user_id: user?.data?.ID,
        platform_id: PLATFORM_ID,
        agent_id: agentObj?.value,
        project_id: 'test',
        mode: agentObj?.mode,
      });
      setSessionID(sessionRes?.session_id);
      queryClient.invalidateQueries({ queryKey: ['sidebar_sessions'] });
      navigate(`/chat/${sessionRes?.session_id}`);
    } catch (error) {
      console.log(error);
      Toast({ type: 'error', message: 'Could not start a chat with this agent' });
    } finally {
      setSwitching(false);
    }
  };

  const handleNewChat = () => {
    const agent = selectedAgent?.value ? selectedAgent : sessionAgent;
    if (!agent?.value) {
      Toast({ type: 'info', message: 'Please select an agent first' });
      return;
    }
    startSessionWith(agent);
  };

  const handleAgentChange = option => {
    if (!option) return;
    // Selecting an agent only updates the active agent — detach any existing session so
    // chat cannot reuse an old session_id until the user creates a new one.
    const agentObj = {
      label: option.label,
      value: option.value,
      icon: option.icon,
      mode: option.mode || 'agent',
    };
    updateAgent(agentObj);
    setSessionAgent(agentObj);
    setChatmessages([]);
    setSessionID(null);
    navigate('/chat');
  };

  const accentColor = '#00dc49';

  return (
    <div className="px-6 py-3 border-b border-shell-border flex items-center gap-3 flex-shrink-0 bg-paper">
      <div
        className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{ backgroundColor: `${accentColor}1A` }}>
        {waitingForAgentData ? (
          <div className="w-full h-full animate-pulse bg-paper-2" aria-hidden="true" />
        ) : iconSrc ? (
          <img src={iconSrc} alt="" className="w-full h-full object-cover" />
        ) : (
          <Bot className="w-4 h-4" style={{ color: accentColor }} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {waitingForAgentData ? (
          <div className="space-y-1.5" aria-hidden="true">
            <div className="h-3.5 w-20 rounded bg-paper-2 animate-pulse" />
            <div className="h-3 w-16 rounded bg-paper-2 animate-pulse" />
          </div>
        ) : (
          <>
            <p className="text-sm font-semibold text-ink truncate">{displayAgent?.label || DEFAULT_COPILOT_NAME}</p>
            <p className="text-xs text-mute">Xavi</p>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <AccentButton type="button" onClick={handleNewChat} disabled={switching || waitingForAgentData} className="px-3 py-1.5 text-sm">
          {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          New Chat
        </AccentButton>
        {agentsLoadingAny && !agentsReady ? (
          <div
            className="h-[34px] min-w-[160px] rounded-full border border-shell-border bg-paper-2 animate-pulse"
            aria-hidden="true"
          />
        ) : (
          <Select
            options={agentOptions}
            value={selectedOption}
            onChange={handleAgentChange}
            isDisabled={switching}
            isSearchable
            placeholder="Assign agent"
            noOptionsMessage={() => 'No agents found'}
            styles={agentSelectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            classNamePrefix="agent-select"
          />
        )}
      </div>
    </div>
  );
}
